import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import { Toaster } from './components/ui/sonner';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import CompletionReport from './pages/reports/CompletionReport';
import AllUpReport from './pages/reports/AllUpReport';
import LearnerActivity from './pages/reports/LearnerActivity';
import CourseActivityReport from './pages/reports/CourseActivityReport';
import CourseEnrollmentsGraph from './pages/reports/CourseEnrollmentsGraph';
import SyncStatus from './pages/admin/SyncStatus';
import LearnersAdmin from './pages/admin/LearnersAdmin';
import CoursesAdmin from './pages/admin/CoursesAdmin';
import LearningJourneysAdmin from './pages/admin/LearningJourneysAdmin';
import UserAccessManagement from './pages/admin/UserAccessManagement';
import PartnerDashboard from './pages/PartnerDashboard';
import PartnerTablePage from './pages/PartnerTablePage';
import MbrDashboard from './pages/reports/MbrDashboard';
import EsiUtilisationReport from './pages/reports/EsiUtilisationReport';
import PartnerSelfServiceDashboard from './pages/reports/PartnerSelfServiceDashboard';
import DealReadyAcceleratorReport from './pages/reports/DealReadyAcceleratorReport';
import JumpstartReport from './pages/reports/JumpstartReport';
import VouchersPage from './pages/reports/VouchersPage';
import CertPrepDashboard from './pages/reports/CertPrepDashboard';
import JourneyReport from './pages/reports/JourneyReport';
import JourneyDetailPage from './pages/reports/JourneyDetailPage';
import LearnerActivityPage from './pages/reports/LearnerActivityPage';
import ByPartnerReport from './pages/reports/ByPartnerReport';
import CompeteHubReport from './pages/reports/CompeteHubReport';
import InsightsCubeCompeteHub from './pages/InsightsCubeCompeteHub';
import InsightsCubeDealReady from './pages/InsightsCubeDealReady';
import ScormCloudActivity from './pages/ScormCloudActivity';
import FeedMirror from './pages/FeedMirror';
import LearningJourneysFeed from './pages/LearningJourneysFeed';
import LearningJourneyEnrolmentsFeed from './pages/LearningJourneyEnrolmentsFeed';
import EventsUpActivity from './pages/EventsUpActivity';
import InsightsCubeCourseActivity from './pages/InsightsCubeCourseActivity';
import InsightsCubeLearners from './pages/InsightsCubeLearners';
import InsightsCubeSkillingHub from './pages/InsightsCubeSkillingHub';
import InsightsCubeCatalog from './pages/InsightsCubeCatalog';
import SkillingInABox from './pages/reports/SkillingInABox';
import CourseLearnerDetail from './pages/reports/CourseLearnerDetail';
import PortalAnalyticsPage from './pages/admin/PortalAnalyticsPage';
import PortalFeedbackPage from './pages/admin/PortalFeedbackPage';
import AccessAuditTrail from './pages/admin/AccessAuditTrail';
import MaicppPartnerRegistryPage from './pages/MaicppPartnerRegistryPage';
import CspResellersPage from './pages/CspResellersPage';
import MaicppDomainsPage from './pages/MaicppDomainsPage';
import ProjectDomainsPage from './pages/ProjectDomainsPage';
import FreeDomainsPage from './pages/FreeDomainsPage';
import BlockedDomainsPage from './pages/BlockedDomainsPage';
import './App.css';

// All API calls include cookies (session-based auth via rpt_session cookie)
axios.defaults.withCredentials = true;

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * ScopeRoute — wraps a route that requires a specific session permission flag.
 * Reads /api/auth/me (same endpoint used throughout the app) and redirects
 * to "/" when the flag is false or absent. Shows nothing while loading
 * (ProtectedRoute handles the full-page spinner above us).
 *
 * flag='show_admin'  → ADMIN section routes
 * flag='is_tf_user'  → PARTNERS section routes
 */
function ScopeRoute({ flag, children }) {
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    axios.get(`${API}/api/auth/me`, { withCredentials: true })
      .then(r => {
        // is_tf_user: also allow when scope.type === 'all' (ADMIN_USERS fallback)
        const allowed =
          flag === 'is_tf_user'
            ? r.data.is_tf_user === true || r.data.scope?.type === 'all'
            : r.data[flag] === true;
        setStatus(allowed ? 'ok' : 'denied');
      })
      .catch(() => setStatus('denied'));
  }, [flag]);

  if (status === 'loading') return null;
  if (status === 'denied') return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <BrowserRouter>
      <Toaster position="top-right" />
      <Routes>
        {/* Public route */}
        <Route path="/login" element={<Login />} />

        {/* Protected routes — all require a valid rpt_session cookie */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="reports/completion" element={<CompletionReport />} />
          <Route path="reports/allup" element={<AllUpReport />} />
          <Route path="reports/learner-activity" element={<LearnerActivity />} />
          <Route path="reports/course-activity" element={<CourseActivityReport />} />
          <Route path="reports/course-activity/:class_id/learners" element={<CourseLearnerDetail />} />
          <Route path="reports/course-enrollments-graph" element={<CourseEnrollmentsGraph />} />
          <Route path="reports/mbr" element={<MbrDashboard />} />
          <Route path="reports/esi-utilisation" element={<ScopeRoute flag="show_admin"><EsiUtilisationReport /></ScopeRoute>} />
          <Route path="reports/partner-self-service" element={<ScopeRoute flag="show_admin"><PartnerSelfServiceDashboard /></ScopeRoute>} />
          <Route path="reports/deal-ready" element={<DealReadyAcceleratorReport />} />
          <Route path="reports/jumpstart" element={<JumpstartReport />} />
          <Route path="reports/vouchers" element={<ScopeRoute flag="is_tf_user"><VouchersPage /></ScopeRoute>} />
          <Route path="reports/cert-prep" element={<CertPrepDashboard />} />
          <Route path="reports/journeys" element={<JourneyReport />} />
          <Route path="reports/journeys/:journeyId" element={<JourneyDetailPage />} />
          <Route path="reports/journeys/:journeyId/learners/:studentUserId" element={<LearnerActivityPage />} />
          <Route path="reports/by-partner" element={<ScopeRoute flag="is_tf_user"><ByPartnerReport /></ScopeRoute>} />
          <Route path="reports/scorm-cloud" element={<ScormCloudActivity />} />
          <Route path="compete-hub" element={<CompeteHubReport />} />
          <Route path="insights/compete-hub" element={<InsightsCubeCompeteHub />} />
          <Route path="insights/deal-ready" element={<Navigate to="/psh-feeds/deal-ready" replace />} />
          <Route path="psh-feeds/deal-ready" element={<ScopeRoute flag="is_tf_user"><InsightsCubeDealReady /></ScopeRoute>} />
          <Route path="insights/scorm-cloud" element={<Navigate to="/reports/scorm-cloud" replace />} />
          <Route path="insights/scrmcld-maq" element={<ScopeRoute flag="is_tf_user"><FeedMirror key="scorm-maq" consumer="maq" kind="scorm-cloud-enrollments" /></ScopeRoute>} />
          <Route path="insights/scrmcld-maqpsh" element={<Navigate to="/insights/scrmcld-maq" replace />} />
          <Route path="insights/eventsup-maq" element={<ScopeRoute flag="is_tf_user"><FeedMirror key="events-maq" consumer="maq" kind="event-attendances" /></ScopeRoute>} />
          <Route path="psh-feeds/eventsup-psh" element={<ScopeRoute flag="is_tf_user"><FeedMirror key="events-psh" consumer="psh" kind="event-attendances" /></ScopeRoute>} />
          <Route path="insights/learning-journeys-maq" element={<ScopeRoute flag="is_tf_user"><LearningJourneysFeed key="lj-maq" consumer="maq" enrolPath="/insights/learning-journey-enrolments-maq" /></ScopeRoute>} />
          <Route path="insights/learning-journey-enrolments-maq" element={<ScopeRoute flag="is_tf_user"><LearningJourneyEnrolmentsFeed key="lje-maq" consumer="maq" /></ScopeRoute>} />
          <Route path="psh-feeds/learning-journeys" element={<ScopeRoute flag="is_tf_user"><LearningJourneysFeed key="lj-psh" consumer="psh" enrolPath="/psh-feeds/learning-journey-enrolments" /></ScopeRoute>} />
          <Route path="psh-feeds/learning-journey-enrolments" element={<ScopeRoute flag="is_tf_user"><LearningJourneyEnrolmentsFeed key="lje-psh" consumer="psh" /></ScopeRoute>} />
          <Route path="psh-feeds/scrmcld-psh" element={<ScopeRoute flag="is_tf_user"><FeedMirror key="scorm-psh" consumer="psh" kind="scorm-cloud-enrollments" /></ScopeRoute>} />
          <Route path="insights/events-up" element={<Navigate to="/linked-platforms/eventsup" replace />} />
          <Route path="insights/course-activity" element={<InsightsCubeCourseActivity />} />
          <Route path="insights/learners" element={<Navigate to="/psh-feeds/learners" replace />} />
          <Route path="psh-feeds/learners" element={<ScopeRoute flag="is_tf_user"><InsightsCubeLearners /></ScopeRoute>} />
          <Route path="insights/skilling-hub-courses" element={<Navigate to="/psh-feeds/courses" replace />} />
          <Route path="psh-feeds/courses" element={<ScopeRoute flag="is_tf_user"><InsightsCubeSkillingHub /></ScopeRoute>} />
          <Route path="insights/catalog" element={<InsightsCubeCatalog />} />
          <Route path="reporting/skilling-box" element={<SkillingInABox />} />
          <Route path="linked-platforms/eventsup" element={<EventsUpActivity />} />
          <Route path="linked-platforms/eventsup/*" element={<Navigate to="/linked-platforms/eventsup" replace />} />
          <Route path="partners" element={<ScopeRoute flag="is_tf_user"><PartnerDashboard /></ScopeRoute>} />
          <Route path="partners/maicpp-registry" element={<ScopeRoute flag="is_tf_user"><MaicppPartnerRegistryPage /></ScopeRoute>} />
          <Route path="partners/table/csp-resellers"   element={<ScopeRoute flag="is_tf_user"><CspResellersPage /></ScopeRoute>} />
          <Route path="partners/table/maicpp-domains"  element={<ScopeRoute flag="is_tf_user"><MaicppDomainsPage /></ScopeRoute>} />
          <Route path="partners/table/project-domains" element={<ScopeRoute flag="is_tf_user"><ProjectDomainsPage /></ScopeRoute>} />
          <Route path="partners/table/free-domains"    element={<ScopeRoute flag="is_tf_user"><FreeDomainsPage /></ScopeRoute>} />
          <Route path="partners/table/blocked-domains" element={<ScopeRoute flag="is_tf_user"><BlockedDomainsPage /></ScopeRoute>} />
          <Route path="partners/table/:tableKey" element={<ScopeRoute flag="is_tf_user"><PartnerTablePage /></ScopeRoute>} />
          <Route path="admin/sync" element={<ScopeRoute flag="show_admin"><SyncStatus /></ScopeRoute>} />
          <Route path="admin/user-access" element={<ScopeRoute flag="show_admin"><UserAccessManagement /></ScopeRoute>} />
          <Route path="admin/learners" element={<ScopeRoute flag="show_admin"><LearnersAdmin /></ScopeRoute>} />
          <Route path="admin/courses" element={<ScopeRoute flag="show_admin"><CoursesAdmin /></ScopeRoute>} />
          <Route path="admin/learning-journeys" element={<ScopeRoute flag="show_admin"><LearningJourneysAdmin /></ScopeRoute>} />
          <Route path="admin/portal/analytics"    element={<ScopeRoute flag="show_admin"><PortalAnalyticsPage /></ScopeRoute>} />
          <Route path="admin/portal/feedback"     element={<ScopeRoute flag="show_admin"><PortalFeedbackPage /></ScopeRoute>} />
          <Route path="admin/portal/access-audit" element={<ScopeRoute flag="show_admin"><AccessAuditTrail /></ScopeRoute>} />
        </Route>

        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
